export type BeneficiaryEntityType = 'PERSONAL' | 'COMPANY';

export type BeneficiaryAccountType = 'BANK_ACCOUNT';

export type PayerEntityType = 'PERSONAL' | 'COMPANY';

export interface AirwallexBeneficiaryAdditionalInfoDto {
  external_identifier: string;
}

export interface AirwallexBeneficiaryBankDetailsDto {
  account_currency: string;
  account_name: string;
  account_routing_type1?: string;
  account_routing_value1?: string;
  account_routing_value2?: string;
  account_number?: string;
  bank_country_code: string;
  bank_name?: string;
  iban?: string;
  bank_account_category?: 'Checking' | 'Savings';
}

export interface AirwallexBeneficiaryInnerDto {
  additional_info?: AirwallexBeneficiaryAdditionalInfoDto;
  address?: BeneficiaryAddressDto;
  bank_details: AirwallexBeneficiaryBankDetailsDto;
  entity_type: BeneficiaryEntityType;
  type: BeneficiaryAccountType;
}

/** Airwallex beneficiary payload returned by the beneficiary-admin endpoint. */
export interface AirwallexBeneficiaryDto {
  beneficiary: AirwallexBeneficiaryInnerDto;
  id: string;
  payer_entity_type: PayerEntityType;
  transfer_methods: string[];
}

export interface CreatorPaymentDetailDto {
  isDeleted: boolean;
  creatorId: string;
  tellerooId: string | null;
  hasConfirmedLargePayment: boolean;
  airwallexId: string | null;
  createdDate: string;
  airwallex?: {
    beneficiary?: {
      bank_details?: Record<string, unknown>;
      entity_type?: string;
      address?: Record<string, unknown>;
    };
    id?: string;
  };
}

export interface BeneficiaryAddressDto {
  street_address: string;
  city: string;
  postcode: string;
  state: string;
  country_code: string;
}

export interface BeneficiaryBankDetailsDto {
  account_name: string;
  account_number: string;
  account_routing_value1?: string;
  account_routing_value2?: string;
  bank_country_code: string;
  account_currency: string;
  bank_account_category?: 'Checking' | 'Savings';
}

export interface CreateShoutBeneficiaryDto {
  entity_type: BeneficiaryEntityType;
  address?: BeneficiaryAddressDto;
  bank_details: BeneficiaryBankDetailsDto;
}

export interface AirwallexValidationErrorResponse {
  code?: string;
  message?: string;
  details?: {
    errors?: Array<{ code?: string; message?: string; source?: string }>;
  };
}

export interface BankCountryPreset {
  bankCountryCode: string;
  currency: string;
  label: string;
}

export const BANK_COUNTRY_PRESETS: readonly BankCountryPreset[] = [
  { bankCountryCode: 'GB', currency: 'GBP', label: 'United Kingdom (GB / GBP)' },
  { bankCountryCode: 'US', currency: 'USD', label: 'United States (US / USD)' },
  { bankCountryCode: 'IE', currency: 'EUR', label: 'Ireland (IE / EUR)' },
  { bankCountryCode: 'AT', currency: 'EUR', label: 'Austria (AT / EUR)' },
  { bankCountryCode: 'BE', currency: 'EUR', label: 'Belgium (BE / EUR)' },
  { bankCountryCode: 'HR', currency: 'EUR', label: 'Croatia (HR / EUR)' },
  { bankCountryCode: 'CY', currency: 'EUR', label: 'Cyprus (CY / EUR)' },
  { bankCountryCode: 'EE', currency: 'EUR', label: 'Estonia (EE / EUR)' },
  { bankCountryCode: 'FI', currency: 'EUR', label: 'Finland (FI / EUR)' },
  { bankCountryCode: 'FR', currency: 'EUR', label: 'France (FR / EUR)' },
  { bankCountryCode: 'DE', currency: 'EUR', label: 'Germany (DE / EUR)' },
  { bankCountryCode: 'GR', currency: 'EUR', label: 'Greece (GR / EUR)' },
  { bankCountryCode: 'IT', currency: 'EUR', label: 'Italy (IT / EUR)' },
  { bankCountryCode: 'LV', currency: 'EUR', label: 'Latvia (LV / EUR)' },
  { bankCountryCode: 'LT', currency: 'EUR', label: 'Lithuania (LT / EUR)' },
  { bankCountryCode: 'LU', currency: 'EUR', label: 'Luxembourg (LU / EUR)' },
  { bankCountryCode: 'MT', currency: 'EUR', label: 'Malta (MT / EUR)' },
  { bankCountryCode: 'NL', currency: 'EUR', label: 'Netherlands (NL / EUR)' },
  { bankCountryCode: 'PT', currency: 'EUR', label: 'Portugal (PT / EUR)' },
  { bankCountryCode: 'SK', currency: 'EUR', label: 'Slovakia (SK / EUR)' },
  { bankCountryCode: 'SI', currency: 'EUR', label: 'Slovenia (SI / EUR)' },
  { bankCountryCode: 'ES', currency: 'EUR', label: 'Spain (ES / EUR)' },
] as const;
